import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  removeCategory,
} from "@/store/categoriesSlice";
import {
  createSubcategory,
  removeSubcategory,
} from "@/store/subcategoriesSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import { buildManageCategoriesCsv } from "@/utils/categoriesCsv";
import { downloadCsvFile } from "@/utils/csv";
import { runManageCategoriesCsvImport } from "@/utils/manageCategoriesCsvImport";
import type { Category, EntryKind } from "@/types";

export type DeleteTarget =
  { type: "category"; id: string } | { type: "subcategory"; id: string } | null;

export function useManagePage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { items: categories } = useAppSelector((s) => s.categories);

  const [manageKind, setManageKind] = useState<EntryKind>("expense");
  const [categoriesCsvImporting, setCategoriesCsvImporting] = useState(false);
  const categoriesCsvInputRef = useRef<HTMLInputElement>(null);

  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<Category | null>(null);
  const [catForm, setCatForm] = useState({ name: "", description: "" });

  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subParentId, setSubParentId] = useState("");
  const [subForm, setSubForm] = useState({ name: "", description: "" });

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const categoriesForKind = useMemo(
    () =>
      [...categories.filter((c) => c.kind === manageKind)].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [categories, manageKind],
  );

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    // Prevent stale edit/create state when switching expense/gain context.
    setEditingCat(null);
    setCatDialogOpen(false);
    setCatForm({ name: "", description: "" });
    setSubDialogOpen(false);
    setSubParentId("");
    setSubForm({ name: "", description: "" });
  }, [manageKind]);

  const openEditCat = useCallback((cat: Category) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, description: cat.description || "" });
    setCatDialogOpen(true);
  }, []);

  const saveCat = useCallback(async () => {
    const data = {
      name: catForm.name,
      description: catForm.description || undefined,
    };
    try {
      if (editingCat && catDialogOpen) {
        await dispatch(updateCategory({ id: editingCat.id, data })).unwrap();
        setCatDialogOpen(false);
        setEditingCat(null);
      } else {
        await dispatch(createCategory({ ...data, kind: manageKind })).unwrap();
      }
      dispatch(fetchCategories());
      setCatForm({ name: "", description: "" });
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  }, [catDialogOpen, catForm, dispatch, editingCat, manageKind, notify]);

  const openCreateSub = useCallback((categoryId: string) => {
    setSubParentId(categoryId);
    setSubForm({ name: "", description: "" });
    setSubDialogOpen(true);
  }, []);

  const saveSub = useCallback(async () => {
    try {
      await dispatch(
        createSubcategory({
          name: subForm.name,
          description: subForm.description || undefined,
          categoryId: subParentId,
        }),
      ).unwrap();
      setSubDialogOpen(false);
      setSubParentId("");
      setSubForm({ name: "", description: "" });
      dispatch(fetchCategories());
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  }, [dispatch, notify, subForm, subParentId]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "category") {
        await dispatch(removeCategory(deleteTarget.id)).unwrap();
      } else {
        await dispatch(removeSubcategory(deleteTarget.id)).unwrap();
      }
      setDeleteTarget(null);
      dispatch(fetchCategories());
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  }, [deleteTarget, dispatch, notify]);

  const exportCategoriesCsv = useCallback(() => {
    const body = buildManageCategoriesCsv(categoriesForKind);
    const stamp = new Date().toISOString().split("T")[0];
    downloadCsvFile(`categories-export-${manageKind}-${stamp}.csv`, body);
  }, [categoriesForKind, manageKind]);

  const onManageCategoriesCsvSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setCategoriesCsvImporting(true);
      try {
        const text = await file.text();
        const result = await runManageCategoriesCsvImport(dispatch, text);
        if (result.fatalReason === "bad_header") {
          notify(t("manage.importCategoriesCsvBadHeader"), "error", 6500);
          return;
        }
        if (result.fatalReason === "empty_file") {
          notify(t("manage.importCategoriesCsvEmpty"), "warning");
          return;
        }
        if (
          result.categoriesCreated === 0 &&
          result.subcategoriesCreated === 0 &&
          result.skippedRows === 0 &&
          result.errors.length === 0
        ) {
          notify(t("manage.importCategoriesCsvNoDataRows"), "info");
          return;
        }
        const summary =
          result.skippedRows > 0
            ? t("manage.importCategoriesCsvSummaryPartial", {
                categories: result.categoriesCreated,
                subcategories: result.subcategoriesCreated,
                skipped: result.skippedRows,
              })
            : t("manage.importCategoriesCsvSummaryOk", {
                categories: result.categoriesCreated,
                subcategories: result.subcategoriesCreated,
              });
        notify(
          summary,
          result.skippedRows > 0 || result.errors.length > 0
            ? "warning"
            : "success",
          6500,
        );
        if (result.errors.length > 0) {
          notify(
            t("manage.importCategoriesCsvErrorsPreview", {
              list: result.errors.slice(0, 6).join(" · "),
            }),
            "info",
            12000,
          );
        }
      } catch (err) {
        notify(extractErrorMessage(err), "error");
      } finally {
        setCategoriesCsvImporting(false);
      }
    },
    [dispatch, notify, t],
  );

  const pickManageCategoriesCsvFile = useCallback(() => {
    categoriesCsvInputRef.current?.click();
  }, []);

  const closeCatDialog = useCallback(() => {
    setCatDialogOpen(false);
    setEditingCat(null);
    setCatForm({ name: "", description: "" });
  }, []);

  const closeSubDialog = useCallback(() => {
    setSubDialogOpen(false);
    setSubParentId("");
    setSubForm({ name: "", description: "" });
  }, []);

  return {
    manageKind,
    setManageKind,
    categoriesForKind,
    catDialogOpen,
    setCatDialogOpen,
    closeCatDialog,
    editingCat,
    catForm,
    setCatForm,
    subDialogOpen,
    setSubDialogOpen,
    closeSubDialog,
    subForm,
    setSubForm,
    deleteTarget,
    setDeleteTarget,
    openEditCat,
    saveCat,
    openCreateSub,
    saveSub,
    handleDelete,
    exportCategoriesCsv,
    categoriesCsvInputRef,
    categoriesCsvImporting,
    onManageCategoriesCsvSelected,
    pickManageCategoriesCsvFile,
  };
}
